import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Briefcase,
  AlertTriangle,
  Gavel,
  FileText,
  Bell,
  MoreHorizontal,
  Trash2,
  Edit3,
  User,
  Palette,
  Check,
  Undo2,
  Repeat,
  X,
} from "lucide-react";
import { calendarApi, clientsApi, mattersApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import SampleBadge from "../ui/SampleBadge";
import CalendarIntegrationBanner from "./CalendarIntegrationBanner";
import SchedulingLinksSection from "./SchedulingLinksSection";
import Toggle from "../ui/Toggle";
import { notifyError } from "../ui/Toast";

// ── Types ────────────────────────────────────────────────────────────

interface CalendarEvent {
  _id: string;
  title: string;
  description: string | null;
  location: string | null;
  startAt: string;
  endAt: string;
  allDay: boolean;
  type: string;
  matterId: { _id: string; title: string; matterNumber: string } | null;
  clientId: { _id: string; displayName: string } | null;
  attendees: {
    userId: unknown;
    email: string | null;
    name: string | null;
    status: string;
  }[];
  recurrence?: {
    type: "none" | "daily" | "weekly" | "monthly" | "yearly";
    interval: number;
    endAt: string | null;
  };
  color: string | null;
  createdAt: string;
  externalSync?: { provider: string | null; externalId: string | null };
  isSample?: boolean;
  isCancelled?: boolean;
  cancellationReason?: string | null;
}

interface ClientOption {
  _id: string;
  displayName: string;
}

interface MatterOption {
  _id: string;
  title: string;
  matterNumber: string;
  clientId?: string;
}

interface FormState {
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  type: string;
  matterId: string;
  clientId: string;
  color: string;
  recurrenceType: "none" | "daily" | "weekly" | "monthly" | "yearly";
  recurrenceInterval: number;
  recurrenceEndAt: string;
}

const RECURRENCE_OPTIONS: { value: FormState["recurrenceType"]; label: string; description: string }[] = [
  { value: "none", label: "Does not repeat", description: "" },
  { value: "daily", label: "Daily", description: "Every day" },
  { value: "weekly", label: "Weekly", description: "Same day each week" },
  { value: "monthly", label: "Monthly", description: "Same date each month" },
  { value: "yearly", label: "Yearly", description: "Same date each year" },
];

// ── Constants ────────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, { bg: string; border: string; text: string; dot: string; custom?: string }> = {
  meeting: { bg: "bg-blue-50", border: "border-l-blue-500", text: "text-blue-700", dot: "bg-blue-500" },
  deadline: { bg: "bg-red-50", border: "border-l-red-500", text: "text-red-700", dot: "bg-red-500" },
  hearing: { bg: "bg-purple-50", border: "border-l-purple-500", text: "text-purple-700", dot: "bg-purple-500" },
  filing: { bg: "bg-amber-50", border: "border-l-amber-500", text: "text-amber-700", dot: "bg-amber-500" },
  reminder: { bg: "bg-slate-50", border: "border-l-slate-400", text: "text-slate-600", dot: "bg-slate-400" },
  other: { bg: "bg-emerald-50", border: "border-l-emerald-500", text: "text-emerald-700", dot: "bg-emerald-500" },
};

const EVENT_ICONS: Record<string, React.FC<{ className?: string }>> = {
  meeting: CalendarIcon,
  deadline: AlertTriangle,
  hearing: Gavel,
  filing: FileText,
  reminder: Bell,
  other: MoreHorizontal,
};

const PRESET_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#8B5CF6",
  "#F59E0B",
  "#10B981",
  "#EC4899",
  "#06B6D4",
  "#6366F1",
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const VIEWS = ["month", "week", "day"] as const;
type ViewType = (typeof VIEWS)[number];

const HOUR_HEIGHT = 60; // px per hour row
const START_HOUR = 0; // 12 AM
const END_HOUR = 24; // 12 AM next day
const TOTAL_HOURS = END_HOUR - START_HOUR; // 24 hours displayed

const EVENT_TYPE_OPTIONS: SelectOption[] = [
  { value: "meeting", label: "Meeting" },
  { value: "deadline", label: "Deadline" },
  { value: "hearing", label: "Hearing" },
  { value: "filing", label: "Filing" },
  { value: "reminder", label: "Reminder" },
  { value: "other", label: "Other" },
];

// ── Helpers ──────────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDateRange(start: string, end: string, allDay: boolean): string {
  if (allDay) return "All day";
  return `${formatTime(start)} - ${formatTime(end)}`;
}

function formatHourLabel(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return "12 PM";
  return `${hour - 12} PM`;
}

function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

function toLocalDatetimeString(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function getEventTopAndHeight(
  event: CalendarEvent
): { top: number; height: number } {
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const gridStartMinutes = START_HOUR * 60;
  const top = ((startMinutes - gridStartMinutes) / 60) * HOUR_HEIGHT;
  const duration = Math.max(endMinutes - startMinutes, 15);
  const height = (duration / 60) * HOUR_HEIGHT;
  return { top: Math.max(top, 0), height: Math.max(height, 20) };
}

function getWeekStartDate(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(date: Date): Date[] {
  const start = getWeekStartDate(date);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function defaultFormState(): FormState {
  const now = new Date();
  const end = new Date(now);
  end.setHours(end.getHours() + 1);
  return {
    title: "",
    description: "",
    location: "",
    startAt: toLocalDatetimeString(now),
    endAt: toLocalDatetimeString(end),
    allDay: false,
    type: "meeting",
    matterId: "",
    clientId: "",
    color: "",
    recurrenceType: "none",
    recurrenceInterval: 1,
    recurrenceEndAt: "",
  };
}

// ── Component ────────────────────────────────────────────────────────

const CalendarPage: React.FC = () => {
  const [view, setView] = useState<ViewType>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormState>(defaultFormState());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteEvent, setConfirmDeleteEvent] = useState(false);

  // Client & Matter dropdowns
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [matters, setMatters] = useState<MatterOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [mattersLoading, setMattersLoading] = useState(false);

  // Preview block shown on grid while create modal is open
  const [previewSlot, setPreviewSlot] = useState<{
    date: Date;
    hour: number;
    minute: number;
  } | null>(null);

  // Drag-to-reschedule state
  const [dragState, setDragState] = useState<{
    eventId: string;
    originalStartAt: string;
    originalEndAt: string;
    offsetMinutes: number; // how many minutes the event has been shifted
  } | null>(null);
  const dragRef = useRef<{
    startY: number;
    didDrag: boolean;
  } | null>(null);

  // Recurring event drag confirmation
  const [recurDragConfirm, setRecurDragConfirm] = useState<{
    eventId: string;
    title: string;
    newStartISO: string;
    newEndISO: string;
    originalStartAt: string;
    originalEndAt: string;
  } | null>(null);

  // Toast shown after drag-reschedule (includes undo data)
  const [dragToast, setDragToast] = useState<{
    title: string;
    time: string;
    eventId: string;
    prevStartAt: string;
    prevEndAt: string;
  } | null>(null);

  const timeGridRef = useRef<HTMLDivElement>(null);
  const currentTimeRef = useRef<HTMLDivElement>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // ── Data Fetching ──────────────────────────────────────────────────

  const getDateRange = useCallback(() => {
    if (view === "month") {
      const start = new Date(year, month, 1);
      start.setDate(start.getDate() - start.getDay());
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, month + 1, 0);
      end.setDate(end.getDate() + (6 - end.getDay()));
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    if (view === "week") {
      const start = getWeekStartDate(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    const start = new Date(currentDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(currentDate);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [view, year, month, currentDate]);

  const initialLoadDone = useRef(false);

  const fetchEvents = useCallback(async () => {
    // Only show full-page spinner on very first load
    if (!initialLoadDone.current) setLoading(true);
    try {
      const { start, end } = getDateRange();
      const res = await calendarApi.list({
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        limit: 200,
      });
      setEvents(res.data?.data || []);
      initialLoadDone.current = true;
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await clientsApi.list({ limit: 200 });
      setClients(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const fetchMatters = useCallback(
    async (clientId?: string) => {
      setMattersLoading(true);
      try {
        const params: Record<string, unknown> = { limit: 200 };
        if (clientId) params.clientId = clientId;
        const res = await mattersApi.list(params);
        setMatters(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch matters:", err);
      } finally {
        setMattersLoading(false);
      }
    },
    []
  );

  // Reset the delete confirmation whenever a different event opens or the modal closes
  useEffect(() => {
    setConfirmDeleteEvent(false);
  }, [selectedEvent?._id, showEventModal]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchClients();
    fetchMatters();
  }, [fetchClients, fetchMatters]);

  // Re-fetch matters when client changes in form
  useEffect(() => {
    if (formData.clientId) {
      fetchMatters(formData.clientId);
    } else {
      fetchMatters();
    }
  }, [formData.clientId, fetchMatters]);

  // Scroll time grid to current time on mount and when view changes
  useEffect(() => {
    if ((view === "week" || view === "day") && !loading && timeGridRef.current) {
      setTimeout(() => {
        const now = new Date();
        const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes();
        const targetScroll = (minutesSinceMidnight / 60) * HOUR_HEIGHT - timeGridRef.current!.clientHeight / 2;
        timeGridRef.current!.scrollTop = Math.max(0, targetScroll);
      }, 100);
    }
  }, [view, loading]);

  // ── Client & Matter SelectOptions ──────────────────────────────────

  const clientOptions: SelectOption[] = useMemo(
    () =>
      clients.map((c) => ({
        value: c._id,
        label: c.displayName,
      })),
    [clients]
  );

  const matterOptions: SelectOption[] = useMemo(
    () =>
      matters.map((m) => ({
        value: m._id,
        label: `${m.matterNumber} - ${m.title}`,
      })),
    [matters]
  );

  // ── Auto-scroll to business hours on load ─────────────────────────
  useEffect(() => {
    if ((view === "week" || view === "day") && timeGridRef.current) {
      // Scroll to 7 AM (7 hours * HOUR_HEIGHT)
      timeGridRef.current.scrollTop = 7 * HOUR_HEIGHT;
    }
  }, [view]);

  // Auto-dismiss drag toast after 4s (longer to give time to undo)
  useEffect(() => {
    if (!dragToast) return;
    const t = setTimeout(() => setDragToast(null), 4000);
    return () => clearTimeout(t);
  }, [dragToast]);

  // Undo last drag move
  const undoDragMove = useCallback(() => {
    if (!dragToast) return;
    const { eventId, prevStartAt, prevEndAt } = dragToast;
    // Optimistic revert
    setEvents((evts) =>
      evts.map((ev) =>
        ev._id === eventId ? { ...ev, startAt: prevStartAt, endAt: prevEndAt } : ev
      )
    );
    // Persist revert
    calendarApi.update(eventId, {
      startAt: prevStartAt,
      endAt: prevEndAt,
    }).catch((err: unknown) => {
      console.error("Failed to undo event move:", err);
      fetchEvents();
    });
    setDragToast(null);
  }, [dragToast, fetchEvents]);

  // Cmd+Z / Ctrl+Z to undo drag
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z" && dragToast) {
        e.preventDefault();
        undoDragMove();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dragToast, undoDragMove]);

  // ── Recurring event drag confirmation handlers ─────────────────────

  const commitRecurDrag = (applyToAll: boolean) => {
    if (!recurDragConfirm) return;
    const { eventId, title, newStartISO, newEndISO, originalStartAt, originalEndAt } = recurDragConfirm;
    const newStart = new Date(newStartISO);
    const newEnd = new Date(newEndISO);
    const timeStr = `${newStart.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${newEnd.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    const dateStr = newStart.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

    // Optimistic local update
    setEvents((evts) =>
      evts.map((ev) =>
        ev._id === eventId ? { ...ev, startAt: newStartISO, endAt: newEndISO } : ev
      )
    );

    setDragToast({
      title,
      time: `${dateStr}, ${timeStr}${applyToAll ? " (all occurrences)" : " (this event only)"}`,
      eventId,
      prevStartAt: originalStartAt,
      prevEndAt: originalEndAt,
    });

    calendarApi.update(eventId, {
      startAt: newStartISO,
      endAt: newEndISO,
    }).catch((err: unknown) => {
      console.error("Failed to reschedule recurring event:", err);
      setEvents((evts) =>
        evts.map((ev) =>
          ev._id === eventId ? { ...ev, startAt: originalStartAt, endAt: originalEndAt } : ev
        )
      );
    });

    setRecurDragConfirm(null);
  };

  const cancelRecurDrag = () => {
    setRecurDragConfirm(null);
  };

  // ── Navigation ─────────────────────────────────────────────────────

  const navigate = (direction: number) => {
    const d = new Date(currentDate);
    if (view === "month") d.setMonth(d.getMonth() + direction);
    else if (view === "week") d.setDate(d.getDate() + 7 * direction);
    else d.setDate(d.getDate() + direction);
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  // ── Event Helpers ──────────────────────────────────────────────────

  const getEventsForDay = useCallback(
    (date: Date): CalendarEvent[] => {
      return events.filter((e) => {
        const start = new Date(e.startAt);
        return isSameDay(start, date);
      });
    },
    [events]
  );

  const getTimedEventsForDay = useCallback(
    (date: Date): CalendarEvent[] => {
      return getEventsForDay(date).filter((e) => !e.allDay);
    },
    [getEventsForDay]
  );

  const getAllDayEventsForDay = useCallback(
    (date: Date): CalendarEvent[] => {
      return getEventsForDay(date).filter((e) => e.allDay);
    },
    [getEventsForDay]
  );

  const getEventColors = (event: CalendarEvent) => {
    // Cancelled events get muted styling
    if (event.isCancelled) {
      return {
        bg: "bg-red-50/60",
        border: "border-l-red-300",
        text: "text-red-400",
        dot: "bg-red-300",
        custom: undefined as string | undefined,
      };
    }
    if (event.color) {
      // Custom hex color selected  -  return inline style overrides
      return {
        bg: "",
        border: "",
        text: "",
        dot: "",
        custom: event.color,
      };
    }
    return EVENT_COLORS[event.type] || EVENT_COLORS.other;
  };

  // ── Event Click & Create ───────────────────────────────────────────

  const handleEventClick = (event: CalendarEvent, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedEvent(event);
    setIsEditing(false);
    setShowEventModal(true);
  };

  const handleSlotClick = (date: Date, hour: number, minute: number = 0) => {
    const startDate = new Date(date);
    startDate.setHours(hour, minute, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + 30);
    setFormData({
      ...defaultFormState(),
      startAt: toLocalDatetimeString(startDate),
      endAt: toLocalDatetimeString(endDate),
    });
    setPreviewSlot({ date, hour, minute });
    setShowCreateModal(true);
  };

  const handleNewEvent = () => {
    setFormData(defaultFormState());
    setShowCreateModal(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    setSaving(true);
    try {
      await calendarApi.create({
        title: formData.title,
        description: formData.description || null,
        location: formData.location || null,
        startAt: new Date(formData.startAt).toISOString(),
        endAt: new Date(formData.endAt).toISOString(),
        allDay: formData.allDay,
        type: formData.type,
        matterId: formData.matterId || null,
        clientId: formData.clientId || null,
        color: formData.color || null,
        recurrence: formData.recurrenceType !== "none" ? {
          type: formData.recurrenceType,
          interval: formData.recurrenceInterval,
          endAt: formData.recurrenceEndAt || null,
        } : { type: "none", interval: 1, endAt: null },
      });
      setShowCreateModal(false); setPreviewSlot(null);
      fetchEvents();
    } catch (err) {
      console.error("Failed to create event:", err);
      notifyError("Couldn't create the event.", err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent || !formData.title.trim()) return;
    setSaving(true);
    try {
      await calendarApi.update(selectedEvent._id, {
        title: formData.title,
        description: formData.description || null,
        location: formData.location || null,
        startAt: new Date(formData.startAt).toISOString(),
        endAt: new Date(formData.endAt).toISOString(),
        allDay: formData.allDay,
        type: formData.type,
        matterId: formData.matterId || null,
        clientId: formData.clientId || null,
        color: formData.color || null,
        recurrence: formData.recurrenceType !== "none" ? {
          type: formData.recurrenceType,
          interval: formData.recurrenceInterval,
          endAt: formData.recurrenceEndAt || null,
        } : { type: "none", interval: 1, endAt: null },
      });
      setShowEventModal(false);
      setSelectedEvent(null);
      setIsEditing(false);
      fetchEvents();
    } catch (err) {
      console.error("Failed to update event:", err);
      notifyError("Couldn't save your changes to the event.", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await calendarApi.remove(id);
      setConfirmDeleteEvent(false);
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (err) {
      console.error("Failed to delete event:", err);
      notifyError("Couldn't delete the event.", err);
    } finally {
      setDeleting(false);
    }
  };

  const startEditing = () => {
    if (!selectedEvent) return;
    setFormData({
      title: selectedEvent.title,
      description: selectedEvent.description || "",
      location: selectedEvent.location || "",
      startAt: toLocalDatetimeString(new Date(selectedEvent.startAt)),
      endAt: toLocalDatetimeString(new Date(selectedEvent.endAt)),
      allDay: selectedEvent.allDay,
      type: selectedEvent.type,
      matterId: selectedEvent.matterId?._id || "",
      clientId: selectedEvent.clientId?._id || "",
      color: selectedEvent.color || "",
      recurrenceType: selectedEvent.recurrence?.type || "none",
      recurrenceInterval: selectedEvent.recurrence?.interval || 1,
      recurrenceEndAt: selectedEvent.recurrence?.endAt
        ? new Date(selectedEvent.recurrence.endAt).toISOString().slice(0, 10)
        : "",
    });
    setIsEditing(true);
  };

  // ── Drag to Reschedule ─────────────────────────────────────────────

  const handleDragStart = (event: CalendarEvent, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startY: e.clientY, didDrag: false };
    setDragState({
      eventId: event._id,
      originalStartAt: event.startAt,
      originalEndAt: event.endAt,
      offsetMinutes: 0,
    });

    const handleMouseMove = (me: MouseEvent) => {
      if (!dragRef.current) return;
      const deltaY = me.clientY - dragRef.current.startY;
      // Only count as a drag if moved more than 3px
      if (Math.abs(deltaY) > 3) dragRef.current.didDrag = true;
      // 5-min increments: HOUR_HEIGHT px = 60 min
      const rawMinutes = (deltaY / HOUR_HEIGHT) * 60;
      const snapped = Math.round(rawMinutes / 5) * 5;
      setDragState((prev) => prev ? { ...prev, offsetMinutes: snapped } : null);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      const didDrag = dragRef.current?.didDrag ?? false;

      // Optimistic update: move the event in local state, persist in background
      setDragState((prev) => {
        if (prev && prev.offsetMinutes !== 0) {
          const newStart = new Date(new Date(prev.originalStartAt).getTime() + prev.offsetMinutes * 60000);
          const newEnd = new Date(new Date(prev.originalEndAt).getTime() + prev.offsetMinutes * 60000);
          const newStartISO = newStart.toISOString();
          const newEndISO = newEnd.toISOString();

          const draggedEvent = events.find((ev) => ev._id === prev.eventId);
          const isRecurring = draggedEvent?.recurrence?.type && draggedEvent.recurrence.type !== "none";

          if (isRecurring) {
            // Show confirmation dialog  -  don't commit yet
            setRecurDragConfirm({
              eventId: prev.eventId,
              title: draggedEvent?.title || "Event",
              newStartISO,
              newEndISO,
              originalStartAt: prev.originalStartAt,
              originalEndAt: prev.originalEndAt,
            });
          } else {
            // Non-recurring: commit immediately
            const evTitle = draggedEvent?.title || "Event";
            const timeStr = `${newStart.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${newEnd.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
            const dateStr = newStart.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

            setEvents((evts) =>
              evts.map((ev) =>
                ev._id === prev.eventId
                  ? { ...ev, startAt: newStartISO, endAt: newEndISO }
                  : ev
              )
            );

            setDragToast({
              title: evTitle,
              time: `${dateStr}, ${timeStr}`,
              eventId: prev.eventId,
              prevStartAt: prev.originalStartAt,
              prevEndAt: prev.originalEndAt,
            });

            calendarApi.update(prev.eventId, {
              startAt: newStartISO,
              endAt: newEndISO,
            }).catch((err: unknown) => {
              console.error("Failed to reschedule event:", err);
              setEvents((evts) =>
                evts.map((ev) =>
                  ev._id === prev.eventId
                    ? { ...ev, startAt: prev.originalStartAt, endAt: prev.originalEndAt }
                    : ev
                )
              );
            });
          }
        }
        return null;
      });

      // If no actual drag happened, treat as a click → open event detail
      if (!didDrag) {
        const clickedEvent = events.find((ev) => ev._id === event._id);
        if (clickedEvent) {
          handleEventClick(clickedEvent);
        }
      }

      dragRef.current = null;
    };

    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleDayClickFromMonth = (date: Date) => {
    setCurrentDate(date);
    setView("day");
  };

  // ── Header Title ───────────────────────────────────────────────────

  const headerTitle = (): string => {
    if (view === "month") {
      return currentDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    if (view === "week") {
      const days = getWeekDays(currentDate);
      const start = days[0];
      const end = days[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()} - ${end.getDate()}, ${end.getFullYear()}`;
      }
      return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return currentDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // ── Current Time Indicator Position ────────────────────────────────

  const now = new Date();
  const currentTimeTop =
    ((now.getHours() * 60 + now.getMinutes() - START_HOUR * 60) / 60) *
    HOUR_HEIGHT;

  // ── Overlap calculation ────────────────────────────────────────────

  function computeOverlapLayout(dayEvents: CalendarEvent[]): Map<string, { left: number; width: number }> {
    const layout = new Map<string, { left: number; width: number }>();
    if (dayEvents.length === 0) return layout;

    const sorted = [...dayEvents].sort((a, b) => {
      const diff = new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
      if (diff !== 0) return diff;
      return new Date(b.endAt).getTime() - new Date(a.endAt).getTime();
    });

    const groups: CalendarEvent[][] = [];
    let currentGroup: CalendarEvent[] = [];
    let groupEnd = 0;

    for (const event of sorted) {
      const start = new Date(event.startAt).getTime();
      const end = new Date(event.endAt).getTime();
      if (currentGroup.length === 0 || start < groupEnd) {
        currentGroup.push(event);
        groupEnd = Math.max(groupEnd, end);
      } else {
        groups.push(currentGroup);
        currentGroup = [event];
        groupEnd = end;
      }
    }
    if (currentGroup.length > 0) groups.push(currentGroup);

    for (const group of groups) {
      const columns: CalendarEvent[][] = [];
      for (const event of group) {
        const eStart = new Date(event.startAt).getTime();
        let placed = false;
        for (let col = 0; col < columns.length; col++) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (new Date(lastInCol.endAt).getTime() <= eStart) {
            columns[col].push(event);
            placed = true;
            break;
          }
        }
        if (!placed) {
          columns.push([event]);
        }
      }
      const totalCols = columns.length;
      for (let col = 0; col < columns.length; col++) {
        for (const event of columns[col]) {
          layout.set(event._id, {
            left: (col / totalCols) * 100,
            width: 100 / totalCols,
          });
        }
      }
    }
    return layout;
  }

  // ── Render: Time Grid Event Block ──────────────────────────────────

  const renderEventBlock = (
    event: CalendarEvent,
    overlapLayout: Map<string, { left: number; width: number }>,
    isWide = false
  ) => {
    const { top, height } = getEventTopAndHeight(event);
    const colors = getEventColors(event);
    const overlap = overlapLayout.get(event._id) || { left: 0, width: 100 };

    const isDragging = dragState?.eventId === event._id;
    const dragOffset = isDragging ? (dragState!.offsetMinutes / 60) * HOUR_HEIGHT : 0;

    // Compute dragged times for overlay
    let dragStartLabel = "";
    let dragEndLabel = "";
    if (isDragging) {
      const newStart = new Date(new Date(dragState!.originalStartAt).getTime() + dragState!.offsetMinutes * 60000);
      const newEnd = new Date(new Date(dragState!.originalEndAt).getTime() + dragState!.offsetMinutes * 60000);
      dragStartLabel = newStart.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      dragEndLabel = newEnd.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }

    return (
      <div key={event._id} className="contents">
        {/* Dragging time overlay  -  shown above the event */}
        {isDragging && (
          <div
            className="absolute z-30 pointer-events-none"
            style={{
              top: `${top + dragOffset - 22}px`,
              left: `calc(${overlap.left}% + 2px)`,
              width: `calc(${overlap.width}% - 4px)`,
            }}
          >
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-800 text-white text-[10px] font-bold shadow-lg whitespace-nowrap">
              {dragStartLabel} – {dragEndLabel}
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          data-event-block
          className={`absolute rounded-lg border-l-[3px] ${colors.border} ${colors.bg} hover:shadow-md overflow-hidden group z-10 pointer-events-auto ${
            isDragging ? "cursor-grabbing shadow-lg ring-2 ring-emerald-400/50 z-20 opacity-90" : "cursor-grab"
          }`}
          style={{
            top: `${top + dragOffset}px`,
            height: `${height}px`,
            left: `calc(${overlap.left}% + 2px)`,
            width: `calc(${overlap.width}% - 4px)`,
            minHeight: "20px",
            transition: isDragging
              ? "none"
              : "left 0.15s ease-out, width 0.15s ease-out, top 0.15s ease-out",
            ...(colors.custom
              ? {
                  backgroundColor: `${colors.custom}15`,
                  borderLeftColor: colors.custom,
                }
              : {}),
          }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            handleDragStart(event, e);
          }}
          onClick={(e) => {
            // Clicks are handled in handleDragStart's mouseUp (click vs drag detection)
            e.stopPropagation();
          }}
        >
          <div className="px-2 py-1 h-full flex flex-col">
            <div className="flex items-center gap-1 truncate">
              {event.isCancelled && (
                <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide text-red-500 bg-red-100 px-1 py-px rounded">
                  Cancelled
                </span>
              )}
              <p
                className={`text-xs font-semibold ${colors.text} truncate leading-tight ${event.isCancelled ? "line-through opacity-60" : ""}`}
                style={colors.custom ? { color: colors.custom } : undefined}
              >
                {event.title}
              </p>
              {event.isSample && <SampleBadge />}
            </div>
            {height > 30 && (
              <p className={`text-[10px] text-slate-500 truncate leading-tight mt-0.5 ${event.isCancelled ? "line-through opacity-60" : ""}`}>
                {isDragging
                  ? `${dragStartLabel} - ${dragEndLabel}`
                  : `${formatTime(event.startAt)} - ${formatTime(event.endAt)}`}
              </p>
            )}
            {isWide && height > 50 && event.isCancelled && event.cancellationReason && (
              <p className="text-[10px] text-red-400 truncate leading-tight mt-0.5 italic">
                {event.cancellationReason}
              </p>
            )}
            {isWide && height > 50 && event.location && !event.isCancelled && (
              <p className="text-[10px] text-slate-400 truncate leading-tight mt-0.5 flex items-center gap-0.5">
                <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                {event.location}
              </p>
            )}
            {isWide && height > 65 && event.matterId && (
              <p className="text-[10px] text-slate-400 truncate leading-tight mt-0.5 flex items-center gap-0.5">
                <Briefcase className="h-2.5 w-2.5 flex-shrink-0" />
                {event.matterId.title}
              </p>
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  // ── Render: Time Grid Column ───────────────────────────────────────

  const renderTimeColumn = (
    date: Date,
    columnIndex: number,
    totalColumns: number,
    isWide = false
  ) => {
    const timedEvents = getTimedEventsForDay(date);
    const today = isToday(date);
    const showCurrentTime =
      today &&
      currentTimeTop >= 0 &&
      currentTimeTop <= TOTAL_HOURS * HOUR_HEIGHT;

    // Build a fake preview event so overlap layout makes room for it
    const PREVIEW_ID = "__preview__";
    let previewEvent: CalendarEvent | null = null;
    if (previewSlot && isSameDay(previewSlot.date, date)) {
      const pStart = new Date(date);
      pStart.setHours(previewSlot.hour, previewSlot.minute, 0, 0);
      const pEnd = new Date(pStart);
      pEnd.setMinutes(pEnd.getMinutes() + 30);
      previewEvent = {
        _id: PREVIEW_ID,
        title: "New Event",
        description: null,
        location: null,
        startAt: pStart.toISOString(),
        endAt: pEnd.toISOString(),
        allDay: false,
        type: "meeting",
        matterId: null,
        clientId: null,
        attendees: [],
        color: null,
        createdAt: "",
      };
    }

    const eventsForLayout = previewEvent
      ? [...timedEvents, previewEvent]
      : timedEvents;
    const overlapLayout = computeOverlapLayout(eventsForLayout);

    // Click on empty space → create event at that time
    const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("[data-event-block]")) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const minutesFromTop = (y / HOUR_HEIGHT) * 60;
      const hour = Math.floor(minutesFromTop / 60) + START_HOUR;
      const quarter = Math.floor((minutesFromTop % 60) / 15);
      const minute = quarter * 15;
      if (hour >= START_HOUR && hour < END_HOUR) {
        handleSlotClick(date, hour, minute);
      }
    };

    return (
      <div
        key={columnIndex}
        className={`relative cursor-pointer ${
          columnIndex < totalColumns - 1 ? "border-r border-slate-200" : ""
        } ${today ? "bg-brand-light/30" : ""}`}
        style={{ minHeight: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}
        onClick={handleBackgroundClick}
      >
        {/* Hour cells with quarter-hour grid lines (background decoration) */}
        {Array.from({ length: TOTAL_HOURS }, (_, i) => {
          const hour = START_HOUR + i;
          return (
            <div
              key={hour}
              className="relative border-b border-slate-100 pointer-events-none"
              style={{ height: `${HOUR_HEIGHT}px` }}
            >
              {[1, 2, 3].map((q) => (
                <div
                  key={q}
                  className="absolute left-0 right-0 border-t border-dashed border-slate-100/60"
                  style={{ top: `${q * (HOUR_HEIGHT / 4)}px` }}
                />
              ))}
            </div>
          );
        })}

        {/* Events  -  pointer-events-none on container so clicks pass through to column,
            each event block has pointer-events-auto to stay clickable */}
        <div className="absolute inset-0 pointer-events-none" style={{ right: "14px" }}>
          {timedEvents.map((event) =>
            renderEventBlock(event, overlapLayout, isWide)
          )}

          {/* Preview block  -  uses overlap layout so existing events split to make room */}
          {previewEvent && overlapLayout.has(PREVIEW_ID) && (() => {
            const { top, height } = getEventTopAndHeight(previewEvent);
            const overlap = overlapLayout.get(PREVIEW_ID)!;
            const startMinutes = previewSlot!.hour * 60 + previewSlot!.minute;
            const endMinutes = startMinutes + 30;
            const endH = Math.floor(endMinutes / 60);
            const endM = endMinutes % 60;
            return (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="absolute rounded-lg border-2 border-dashed border-brand-accent bg-brand-light/70 z-10 overflow-hidden"
                style={{
                  top: `${top}px`,
                  height: `${Math.max(height, 20)}px`,
                  left: `calc(${overlap.left}% + 2px)`,
                  width: `calc(${overlap.width}% - 4px)`,
                  transition: "left 0.15s ease-out, width 0.15s ease-out",
                }}
              >
                <div className="px-2 py-1">
                  <p className="text-xs font-semibold text-brand truncate">
                    New Event
                  </p>
                  <p className="text-[10px] text-brand-accent/80 truncate">
                    {formatHourLabel(previewSlot!.hour).replace(" ", "")}{previewSlot!.minute ? `:${String(previewSlot!.minute).padStart(2, "0")}` : ""} – {formatHourLabel(endH).replace(" ", "")}{endM ? `:${String(endM).padStart(2, "0")}` : ""}
                  </p>
                </div>
              </motion.div>
            );
          })()}
        </div>

        {/* Current time indicator */}
        {showCurrentTime && (
          <div
            ref={currentTimeRef}
            className="absolute left-0 right-0 z-20 pointer-events-none"
            style={{ top: `${currentTimeTop}px` }}
          >
            <div className="relative flex items-center">
              <div className="h-3 w-3 rounded-full bg-red-500 -ml-1.5 shadow-sm" />
              <div className="flex-1 h-[2px] bg-red-500" />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Week View ──────────────────────────────────────────────────────

  const renderWeekView = () => {
    const days = getWeekDays(currentDate);
    const allDayByDay = days.map((d) => getAllDayEventsForDay(d));
    const hasAllDay = allDayByDay.some((arr) => arr.length > 0);

    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col"
      >
        {/* Column Headers */}
        <div className="flex border-b border-slate-200 flex-shrink-0">
          {/* Time gutter spacer */}
          <div className="w-16 flex-shrink-0 border-r border-slate-200" />
          {/* Day headers */}
          <div className="flex-1 grid grid-cols-7">
            {days.map((d, i) => {
              const today = isToday(d);
              return (
                <div
                  key={i}
                  className={`text-center py-3 ${
                    i < 6 ? "border-r border-slate-200" : ""
                  } ${today ? "bg-brand-light/50" : ""}`}
                >
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {DAYS[i]}
                  </div>
                  <div
                    className={`text-lg font-bold mt-0.5 ${
                      today ? "text-white" : "text-slate-700"
                    }`}
                  >
                    {today ? (
                      <span
                        className="inline-flex items-center justify-center h-8 w-8 rounded-full"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        {d.getDate()}
                      </span>
                    ) : (
                      d.getDate()
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* All-day events row */}
        {hasAllDay && (
          <div className="flex border-b border-slate-200 flex-shrink-0">
            <div className="w-16 flex-shrink-0 border-r border-slate-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-slate-400 uppercase">
                All day
              </span>
            </div>
            <div className="flex-1 grid grid-cols-7">
              {allDayByDay.map((dayAllDay, i) => (
                <div
                  key={i}
                  className={`p-1 min-h-[32px] ${
                    i < 6 ? "border-r border-slate-200" : ""
                  }`}
                >
                  {dayAllDay.map((event) => {
                    const colors = getEventColors(event);
                    return (
                      <div
                        key={event._id}
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded truncate cursor-pointer flex items-center gap-1 ${colors.bg} ${colors.text}`}
                        style={colors.custom ? { backgroundColor: `${colors.custom}15`, color: colors.custom } : undefined}
                        onClick={(e) => handleEventClick(event, e)}
                      >
                        <span className={event.isCancelled ? "line-through opacity-60" : ""}>{event.title}</span>
                        {event.isSample && <SampleBadge />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time grid */}
        <div
          ref={timeGridRef}
          className="flex-1 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 280px)" }}
        >
          <div className="flex">
            {/* Time gutter */}
            <div className="w-16 flex-shrink-0 border-r border-slate-200">
              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                <div
                  key={i}
                  className="relative border-b border-slate-100"
                  style={{ height: `${HOUR_HEIGHT}px` }}
                >
                  <span className="absolute -top-2.5 right-2 text-[10px] font-medium text-slate-400">
                    {formatHourLabel(START_HOUR + i)}
                  </span>
                </div>
              ))}
            </div>
            {/* Day columns */}
            <div className="flex-1 grid grid-cols-7">
              {days.map((d, i) => renderTimeColumn(d, i, 7))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // ── Day View ───────────────────────────────────────────────────────

  const renderDayView = () => {
    const allDayEvents = getAllDayEventsForDay(currentDate);
    const hasAllDay = allDayEvents.length > 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col"
      >
        {/* Day Header */}
        <div className="flex border-b border-slate-200 flex-shrink-0">
          <div className="w-16 flex-shrink-0 border-r border-slate-200" />
          <div
            className={`flex-1 py-4 px-4 ${isToday(currentDate) ? "bg-brand-light/50" : ""}`}
          >
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              {currentDate.toLocaleDateString("en-US", { weekday: "long" })}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {isToday(currentDate) ? (
                <span
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full text-xl font-bold text-white"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {currentDate.getDate()}
                </span>
              ) : (
                <span className="text-xl font-bold text-slate-700">
                  {currentDate.getDate()}
                </span>
              )}
              <span className="text-sm text-slate-500">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* All-day events */}
        {hasAllDay && (
          <div className="flex border-b border-slate-200 flex-shrink-0">
            <div className="w-16 flex-shrink-0 border-r border-slate-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-slate-400 uppercase">
                All day
              </span>
            </div>
            <div className="flex-1 p-2 flex flex-wrap gap-1">
              {allDayEvents.map((event) => {
                const colors = getEventColors(event);
                return (
                  <div
                    key={event._id}
                    className={`text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer flex items-center gap-1.5 ${colors.bg} ${colors.text} border-l-[3px] ${colors.border}`}
                    style={colors.custom ? { backgroundColor: `${colors.custom}15`, color: colors.custom, borderLeftColor: colors.custom } : undefined}
                    onClick={(e) => handleEventClick(event, e)}
                  >
                    {event.isCancelled && (
                      <span className="text-[9px] font-bold uppercase text-red-500 bg-red-100 px-1 py-px rounded flex-shrink-0">
                        Cancelled
                      </span>
                    )}
                    <span className={event.isCancelled ? "line-through opacity-60" : ""}>
                      {event.title}
                    </span>
                    {event.isSample && <SampleBadge />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Time grid */}
        <div
          ref={timeGridRef}
          className="flex-1 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 280px)" }}
        >
          <div className="flex">
            {/* Time gutter */}
            <div className="w-16 flex-shrink-0 border-r border-slate-200">
              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                <div
                  key={i}
                  className="relative border-b border-slate-100"
                  style={{ height: `${HOUR_HEIGHT}px` }}
                >
                  <span className="absolute -top-2.5 right-2 text-[10px] font-medium text-slate-400">
                    {formatHourLabel(START_HOUR + i)}
                  </span>
                </div>
              ))}
            </div>
            {/* Single wide column */}
            <div className="flex-1">
              {renderTimeColumn(currentDate, 0, 1, true)}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // ── Month View ─────────────────────────────────────────────────────

  const renderMonthView = () => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const cells: React.ReactNode[] = [];

    // Empty cells before month start
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = 0; i < firstDay; i++) {
      const dayNum = prevMonthDays - firstDay + 1 + i;
      cells.push(
        <div
          key={`prev-${i}`}
          className="min-h-[110px] bg-slate-50/50 border-b border-r border-slate-200 p-1.5"
        >
          <span className="text-sm font-medium text-slate-300 px-1">
            {dayNum}
          </span>
        </div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getEventsForDay(date);
      const today = isToday(date);

      cells.push(
        <div
          key={day}
          className={`min-h-[110px] border-b border-r border-slate-200 p-1.5 cursor-pointer hover:bg-slate-50 transition-colors ${
            today ? "bg-brand-light/30" : ""
          }`}
          onClick={() => handleDayClickFromMonth(date)}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-sm font-semibold inline-flex items-center justify-center h-6 w-6 rounded-full ${
                today ? "text-white" : "text-slate-600"
              }`}
              style={today ? { backgroundColor: "var(--color-primary)" } : {}}
            >
              {day}
            </span>
          </div>
          <div className="space-y-0.5">
            {dayEvents.slice(0, 3).map((event) => {
              const colors = getEventColors(event);
              return (
                <div
                  key={event._id}
                  className={`text-[11px] px-1.5 py-0.5 rounded truncate flex items-center gap-1 ${colors.bg} ${colors.text} cursor-pointer hover:shadow-sm transition-shadow`}
                  style={colors.custom ? { backgroundColor: `${colors.custom}15`, color: colors.custom } : undefined}
                  onClick={(e) => handleEventClick(event, e)}
                >
                  {event.isCancelled && (
                    <span className="text-[8px] font-bold uppercase text-red-500 flex-shrink-0">X</span>
                  )}
                  {!event.allDay && (
                    <span className={`font-semibold ${event.isCancelled ? "line-through opacity-60" : ""}`}>
                      {formatTime(event.startAt)}{" "}
                    </span>
                  )}
                  <span className={`truncate ${event.isCancelled ? "line-through opacity-60" : ""}`}>{event.title}</span>
                  {event.isSample && <SampleBadge />}
                </div>
              );
            })}
            {dayEvents.length > 3 && (
              <div className="text-[11px] text-slate-500 font-medium px-1.5 hover:text-slate-700 transition-colors">
                +{dayEvents.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }

    // Fill remaining cells
    const totalCells = cells.length;
    const remainder = totalCells % 7;
    if (remainder > 0) {
      for (let i = 1; i <= 7 - remainder; i++) {
        cells.push(
          <div
            key={`next-${i}`}
            className="min-h-[110px] bg-slate-50/50 border-b border-r border-slate-200 p-1.5"
          >
            <span className="text-sm font-medium text-slate-300 px-1">
              {i}
            </span>
          </div>
        );
      }
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {DAYS.map((day) => (
            <div
              key={day}
              className="text-center py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-r border-slate-200 last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7">{cells}</div>
      </motion.div>
    );
  };

  // ── Render: Event Form (shared between create & edit) ──────────────

  const renderEventForm = (
    formId: string,
    onSubmit: (e: React.FormEvent) => void
  ) => (
    <form id={formId} onSubmit={onSubmit} className="space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          placeholder="Event title"
        />
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Type
        </label>
        <SearchableSelect
          options={EVENT_TYPE_OPTIONS}
          value={formData.type}
          onChange={(val) =>
            setFormData({ ...formData, type: val || "meeting" })
          }
          placeholder="Select event type"
        />
      </div>

      {/* All Day Toggle */}
      <div className="flex items-center gap-3">
        <Toggle
          checked={formData.allDay}
          onChange={(val) => setFormData({ ...formData, allDay: val })}
        />
        <label className="text-sm text-slate-700 font-medium">
          All day event
        </label>
      </div>

      {/* Date / Time */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Start <span className="text-red-500">*</span>
          </label>
          <input
            type={formData.allDay ? "date" : "datetime-local"}
            required
            value={
              formData.allDay
                ? formData.startAt.slice(0, 10)
                : formData.startAt
            }
            onChange={(e) =>
              setFormData({ ...formData, startAt: e.target.value })
            }
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            End <span className="text-red-500">*</span>
          </label>
          <input
            type={formData.allDay ? "date" : "datetime-local"}
            required
            value={
              formData.allDay ? formData.endAt.slice(0, 10) : formData.endAt
            }
            onChange={(e) =>
              setFormData({ ...formData, endAt: e.target.value })
            }
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>
      </div>

      {/* Recurrence */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <div className="flex items-center gap-1.5">
            <Repeat className="h-3.5 w-3.5" />
            Repeat
          </div>
        </label>
        <div className="flex flex-wrap gap-1.5">
          {RECURRENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  recurrenceType: opt.value,
                  recurrenceInterval: opt.value === "none" ? 1 : formData.recurrenceInterval,
                  recurrenceEndAt: opt.value === "none" ? "" : formData.recurrenceEndAt,
                })
              }
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                formData.recurrenceType === opt.value
                  ? "text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={
                formData.recurrenceType === opt.value
                  ? { backgroundColor: "var(--color-primary)" }
                  : {}
              }
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Recurrence details  -  shown when repeat is enabled */}
        <AnimatePresence>
          {formData.recurrenceType !== "none" && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                {/* Interval */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 font-medium whitespace-nowrap">Every</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={formData.recurrenceInterval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recurrenceInterval: Math.max(1, parseInt(e.target.value) || 1),
                      })
                    }
                    className="w-16 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-center focus:outline-none focus:border-emerald-600"
                  />
                  <span className="text-xs text-slate-600 font-medium">
                    {formData.recurrenceType === "daily" && (formData.recurrenceInterval === 1 ? "day" : "days")}
                    {formData.recurrenceType === "weekly" && (formData.recurrenceInterval === 1 ? "week" : "weeks")}
                    {formData.recurrenceType === "monthly" && (formData.recurrenceInterval === 1 ? "month" : "months")}
                    {formData.recurrenceType === "yearly" && (formData.recurrenceInterval === 1 ? "year" : "years")}
                  </span>
                </div>

                {/* End date */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 font-medium whitespace-nowrap">Ends</span>
                  <div className="flex items-center gap-2 flex-1">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, recurrenceEndAt: "" })}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        !formData.recurrenceEndAt
                          ? "bg-slate-700 text-white"
                          : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      Never
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!formData.recurrenceEndAt) {
                          const d = new Date(formData.startAt);
                          d.setMonth(d.getMonth() + 3);
                          setFormData({ ...formData, recurrenceEndAt: d.toISOString().slice(0, 10) });
                        }
                      }}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        formData.recurrenceEndAt
                          ? "bg-slate-700 text-white"
                          : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      On date
                    </button>
                    {formData.recurrenceEndAt && (
                      <input
                        type="date"
                        value={formData.recurrenceEndAt}
                        onChange={(e) =>
                          setFormData({ ...formData, recurrenceEndAt: e.target.value })
                        }
                        className="flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    )}
                  </div>
                </div>

                {/* Summary */}
                <p className="text-[11px] text-slate-500 italic">
                  Repeats every{" "}
                  {formData.recurrenceInterval > 1 ? `${formData.recurrenceInterval} ` : ""}
                  {formData.recurrenceType === "daily" && (formData.recurrenceInterval === 1 ? "day" : "days")}
                  {formData.recurrenceType === "weekly" && (formData.recurrenceInterval === 1 ? "week" : "weeks")}
                  {formData.recurrenceType === "monthly" && (formData.recurrenceInterval === 1 ? "month" : "months")}
                  {formData.recurrenceType === "yearly" && (formData.recurrenceInterval === 1 ? "year" : "years")}
                  {formData.recurrenceEndAt
                    ? ` until ${new Date(formData.recurrenceEndAt + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`
                    : ", forever"}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Location
        </label>
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            placeholder="Location or virtual meeting link"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors resize-none"
          rows={3}
          placeholder="Add details about this event"
        />
      </div>

      {/* Client */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Client
        </label>
        <SearchableSelect
          options={clientOptions}
          value={formData.clientId || null}
          onChange={(val) =>
            setFormData({ ...formData, clientId: val || "", matterId: "" })
          }
          placeholder="Select a client (optional)"
          icon={User}
          loading={clientsLoading}
          clearable
        />
      </div>

      {/* Matter */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Matter
        </label>
        <SearchableSelect
          options={matterOptions}
          value={formData.matterId || null}
          onChange={(val) =>
            setFormData({ ...formData, matterId: val || "" })
          }
          placeholder="Select a matter (optional)"
          icon={Briefcase}
          loading={mattersLoading}
          clearable
        />
      </div>

      {/* Color */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          <div className="flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            Color (optional)
          </div>
        </label>
        <div className="flex items-center gap-2">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                formData.color === color
                  ? "border-slate-800 scale-110"
                  : "border-transparent"
              }`}
              style={{ backgroundColor: color }}
              onClick={() =>
                setFormData({
                  ...formData,
                  color: formData.color === color ? "" : color,
                })
              }
            />
          ))}
          {formData.color && (
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700 ml-1"
              onClick={() => setFormData({ ...formData, color: "" })}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </form>
  );

  // ── Main Render ────────────────────────────────────────────────────

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Calendar</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your events, deadlines, and hearings
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleNewEvent}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors shadow-sm"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--color-primary)")
          }
        >
          <Plus className="h-5 w-5" />
          New Event
        </motion.button>
      </div>

      {/* Calendar Integration Banner */}
      <CalendarIntegrationBanner onIntegrationChange={fetchEvents} />

      {/* Scheduling Links Section */}
      <SchedulingLinksSection />

      {/* Toolbar */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Left: Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-slate-600" />
                </button>
                <button
                  onClick={goToToday}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-700"
                >
                  {(() => {
                    const now = new Date();
                    if (view === "week") {
                      const days = getWeekDays(currentDate);
                      const includesNow = days.some((d) => isSameDay(d, now));
                      if (includesNow) return "This Week";
                      const s = days[0];
                      const e = days[6];
                      const sameMonth = s.getMonth() === e.getMonth();
                      return sameMonth
                        ? `${s.toLocaleDateString([], { month: "short", day: "numeric" })} – ${e.getDate()}`
                        : `${s.toLocaleDateString([], { month: "short", day: "numeric" })} – ${e.toLocaleDateString([], { month: "short", day: "numeric" })}`;
                    }
                    if (view === "month") {
                      const sameMonth = now.getMonth() === currentDate.getMonth() && now.getFullYear() === currentDate.getFullYear();
                      return sameMonth ? "This Month" : currentDate.toLocaleDateString([], { month: "long", year: "numeric" });
                    }
                    return isSameDay(currentDate, now) ? "Today" : currentDate.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
                  })()}
                </button>
                <button
                  onClick={() => navigate(1)}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-slate-600" />
                </button>
                <h2 className="text-lg font-bold text-slate-800 ml-3">
                  {headerTitle()}
                </h2>
              </div>

              {/* Right: View Switcher */}
              <div className="flex rounded-xl bg-slate-100 p-1">
                {VIEWS.map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                      view === v
                        ? "text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-800"
                    }`}
                    style={view === v ? { backgroundColor: "var(--color-primary)" } : {}}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar Content  -  Full Width */}
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex flex-col items-center gap-3">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-[3px] border-slate-200"
                  style={{ borderTopColor: "var(--color-primary)" }}
                />
                <span className="text-sm text-slate-500 font-medium">
                  Loading events...
                </span>
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {view === "week" && <React.Fragment key="week">{renderWeekView()}</React.Fragment>}
              {view === "day" && <React.Fragment key="day">{renderDayView()}</React.Fragment>}
              {view === "month" && <React.Fragment key="month">{renderMonthView()}</React.Fragment>}
            </AnimatePresence>
          )}

      {/* ── Create Event Modal ────────────────────────────────────────── */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); setPreviewSlot(null); }}
        title="New Event"
        subtitle="Create a new calendar event"
        icon={CalendarIcon}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { setShowCreateModal(false); setPreviewSlot(null); }}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="event-create-form"
              disabled={saving || !formData.title.trim()}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                if (!saving && formData.title.trim())
                  e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "var(--color-primary)")
              }
            >
              {saving ? "Creating..." : "Create Event"}
            </button>
          </div>
        }
      >
        {renderEventForm("event-create-form", handleCreate)}
      </PremiumModal>

      {/* ── Event Detail / Edit Modal ─────────────────────────────────── */}
      <PremiumModal
        open={showEventModal && !!selectedEvent}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
          setIsEditing(false);
        }}
        title={
          isEditing ? "Edit Event" : selectedEvent?.title || "Event Details"
        }
        subtitle={
          isEditing
            ? "Update event details"
            : selectedEvent
              ? selectedEvent.type.charAt(0).toUpperCase() +
                selectedEvent.type.slice(1)
              : ""
        }
        icon={isEditing ? Edit3 : CalendarIcon}
        size="lg"
        footer={
          selectedEvent ? (
            isEditing ? (
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel Edit
                </button>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    form="event-edit-form"
                    disabled={saving || !formData.title.trim()}
                    className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) => {
                      if (!saving && formData.title.trim())
                        e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
                    }}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                    }
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            ) : confirmDeleteEvent ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-red-600 font-medium">Are you sure you want to delete this event?</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteEvent(false)}
                    className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(selectedEvent._id)}
                    disabled={deleting}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                <button
                  onClick={() => setConfirmDeleteEvent(true)}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? "Deleting..." : "Delete"}
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={startEditing}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setShowEventModal(false);
                      setSelectedEvent(null);
                    }}
                    className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
                    style={{ backgroundColor: "var(--color-primary)" }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                    }
                  >
                    Close
                  </button>
                </div>
              </div>
            )
          ) : undefined
        }
      >
        {selectedEvent &&
          (isEditing ? (
            renderEventForm("event-edit-form", handleUpdate)
          ) : (
            <div className="space-y-5">
              {/* Type Badge + Color */}
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    getEventColors(selectedEvent).bg
                  } ${getEventColors(selectedEvent).text}`}
                  style={getEventColors(selectedEvent).custom ? { backgroundColor: `${getEventColors(selectedEvent).custom}15`, color: getEventColors(selectedEvent).custom } : undefined}
                >
                  {(() => {
                    const Icon =
                      EVENT_ICONS[selectedEvent.type] || EVENT_ICONS.other;
                    return <Icon className="h-3 w-3" />;
                  })()}
                  {selectedEvent.type.charAt(0).toUpperCase() +
                    selectedEvent.type.slice(1)}
                </span>
                {selectedEvent.color && (
                  <span
                    className="h-5 w-5 rounded-full border border-white shadow-sm"
                    style={{ backgroundColor: selectedEvent.color }}
                  />
                )}
                {selectedEvent.recurrence?.type && selectedEvent.recurrence.type !== "none" && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">
                    <Repeat className="h-3 w-3" />
                    {selectedEvent.recurrence.type.charAt(0).toUpperCase() + selectedEvent.recurrence.type.slice(1)}
                    {selectedEvent.recurrence.interval > 1 && ` (every ${selectedEvent.recurrence.interval})`}
                  </span>
                )}
                {selectedEvent.externalSync?.provider && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">
                    Synced: {selectedEvent.externalSync.provider}
                  </span>
                )}
              </div>

              {/* Cancelled Banner */}
              {selectedEvent.isCancelled && (
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-200">
                  <X className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-700">
                      This booking was cancelled
                    </p>
                    {selectedEvent.cancellationReason && (
                      <p className="text-sm text-red-600 mt-0.5">
                        Reason: {selectedEvent.cancellationReason}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Date & Time */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <Clock className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {new Date(selectedEvent.startAt).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      }
                    )}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {formatDateRange(
                      selectedEvent.startAt,
                      selectedEvent.endAt,
                      selectedEvent.allDay
                    )}
                  </p>
                </div>
              </div>

              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">
                      Location
                    </p>
                    <p className="text-sm text-slate-700">
                      {selectedEvent.location}
                    </p>
                  </div>
                </div>
              )}

              {/* Client */}
              {selectedEvent.clientId && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <User className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">
                      Client
                    </p>
                    <p className="text-sm font-medium text-slate-700">
                      {selectedEvent.clientId.displayName}
                    </p>
                  </div>
                </div>
              )}

              {/* Matter */}
              {selectedEvent.matterId && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <Briefcase className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">
                      Matter
                    </p>
                    <p className="text-sm font-medium text-slate-700">
                      {selectedEvent.matterId.matterNumber} -{" "}
                      {selectedEvent.matterId.title}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedEvent.description && (
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                    Description
                  </p>
                  <div className="p-3 bg-slate-50 rounded-xl text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {selectedEvent.description}
                  </div>
                </div>
              )}

              {/* Attendees */}
              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                    Attendees
                  </p>
                  <div className="space-y-1.5">
                    {selectedEvent.attendees.map((a, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                      >
                        <div
                          className="h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ backgroundColor: "var(--color-accent)" }}
                        >
                          {(a.name || a.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {a.name || a.email || "Unknown"}
                          </p>
                          {a.email && a.name && (
                            <p className="text-xs text-slate-400 truncate">
                              {a.email}
                            </p>
                          )}
                        </div>
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                            a.status === "accepted"
                              ? "bg-emerald-100 text-emerald-700"
                              : a.status === "declined"
                                ? "bg-red-100 text-red-700"
                                : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {a.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Created At */}
              <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                Created{" "}
                {new Date(selectedEvent.createdAt).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            </div>
          ))}
      </PremiumModal>

      {/* Recurring drag confirmation dialog */}
      <AnimatePresence>
        {recurDragConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={cancelRecurDrag}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}
                  >
                    <Repeat className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800">
                      Move recurring event
                    </h3>
                    <p className="text-xs text-slate-500">
                      &ldquo;{recurDragConfirm.title}&rdquo;
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-5">
                  This is a recurring event. Would you like to move just this occurrence or all future events?
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => commitRecurDrag(false)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-left group"
                  >
                    <CalendarIcon className="h-5 w-5 text-slate-400 group-hover:text-slate-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-slate-700">This event only</p>
                      <p className="text-xs text-slate-500">Only this occurrence will be moved</p>
                    </div>
                  </button>
                  <button
                    onClick={() => commitRecurDrag(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors text-left group"
                  >
                    <Repeat className="h-5 w-5 text-slate-400 group-hover:text-slate-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-slate-700">All events</p>
                      <p className="text-xs text-slate-500">All occurrences will be moved to the new time</p>
                    </div>
                  </button>
                </div>
              </div>
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
                <button
                  onClick={cancelRecurDrag}
                  className="w-full text-center text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors py-1"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drag-reschedule toast */}
      <AnimatePresence>
        {dragToast && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-800 text-white shadow-2xl border border-slate-700">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgba(15, 81, 50, 0.3)" }}
              >
                <Check className="h-4 w-4" style={{ color: "#34d399" }} />
              </div>
              <div className="mr-2">
                <p className="text-sm font-semibold">{dragToast.title} moved</p>
                <p className="text-xs text-slate-300">{dragToast.time}</p>
              </div>
              <button
                onClick={undoDragMove}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-xs font-semibold text-white whitespace-nowrap border border-white/10"
              >
                <Undo2 className="h-3.5 w-3.5" />
                Undo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarPage;
